import { Loader2, Mail } from 'lucide-react';
import type { TrialEmailType } from '@signalvote/shared';
import { useCampaignStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';
import { useTrialEmailAudit, useSendTrialEmail } from '@/hooks/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// AC-pinned format: `Apr 20, 2026 14:03 UTC` — short month, 4-digit year, 24h time,
// explicit "UTC" suffix. UTC is intentional: the audit tool is used by admins across
// geographies and often paired with log screenshots; consistent wall-clock beats
// browser-local friendliness here.
function formatSentAtUtc(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const find = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const month = find('month');
  const day = find('day');
  const year = find('year');
  // Intl can return "24" for midnight under hour12:false — normalize to "00" so the
  // rendered string always matches HH:mm.
  const hourRaw = find('hour');
  const hour = hourRaw === '24' ? '00' : hourRaw;
  const minute = find('minute');
  return `${month} ${day}, ${year} ${hour}:${minute} UTC`;
}

type RowSpec = {
  label: string;
  description: string;
  type: TrialEmailType;
  sentAt: string | null;
};

const RESEND_CONFIRM_MESSAGE =
  'Resend this email? The owner will receive it immediately.';

export function TrialEmailAuditCard() {
  const { user } = useAuth();
  const selectedCampaign = useCampaignStore((s) => s.selectedCampaign);
  // Gate the GET behind the same visibility check as the render itself — prevents
  // 403-spam on every settings-page load for non-admins (FullAdmins, campaign
  // owners, FieldDirectors all reach the settings hub that mounts this card).
  const isVisible = !!user?.isPlatformAdmin && !!selectedCampaign;
  const { data, isLoading } = useTrialEmailAudit({ enabled: isVisible });
  const { mutate: send, isPending, variables } = useSendTrialEmail();

  if (!isVisible) return null;

  const rows: RowSpec[] = [
    {
      label: 'T-3d',
      description: '3 days before trial ends',
      type: 'T3d',
      sentAt: data?.t3dSentAt ?? null,
    },
    {
      label: 'T-1d',
      description: '1 day before trial ends',
      type: 'T1d',
      sentAt: data?.t1dSentAt ?? null,
    },
    {
      label: 'T+1d',
      description: '1 day after trial ends',
      type: 'T1dAfter',
      sentAt: data?.t1dAfterSentAt ?? null,
    },
  ];

  const handleClick = (row: RowSpec) => {
    if (row.sentAt) {
      // Already sent → AC-pinned confirm() for resend
      if (!window.confirm(RESEND_CONFIRM_MESSAGE)) return;
    }
    send(row.type);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Trial Email Audit
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Per-campaign send history for the three trial nudge emails. Resending
          bypasses the scheduled-job skip conditions (active subscription, window
          timing, etc.) — use for debugging or recovery.
        </p>

        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => {
              const buttonLabel = row.sentAt ? 'Resend' : 'Send now';
              const isThisPending = isPending && variables === row.type;
              return (
                <div
                  key={row.type}
                  className="flex items-start justify-between gap-3 border-t pt-3 first:border-t-0 first:pt-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {row.label}{' '}
                      <span className="text-xs text-muted-foreground font-normal">
                        ({row.description})
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {row.sentAt
                        ? `Sent: ${formatSentAtUtc(row.sentAt)}`
                        : 'Not yet sent'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant={row.sentAt ? 'outline' : 'default'}
                    onClick={() => handleClick(row)}
                    disabled={isPending}
                  >
                    {isThisPending ? (
                      <>
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      buttonLabel
                    )}
                  </Button>
                </div>
              );
            })}

            {data?.trialEndsAtFormatted && (
              <p className="text-xs text-muted-foreground pt-2 border-t">
                Trial ends: {data.trialEndsAtFormatted}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
